import { deferTracking, sendGAEvent } from '@noths/polaris-client-google-analytics';

import type { MediaLocation } from 'src/components/organisms/ProductMediaCarousel/types';

/**
 * PDP product-video analytics.
 * The impression maps to the `view_content` pipeline; every interaction maps to
 * `content_interaction`. `item_id` is the Mux playback id (the join key to QoS),
 * `content_position` is the 1-based carousel position and `total_options` is the
 * media count (impression only). `location` distinguishes the main carousel from
 * the expanded full-screen view, mirroring the image-carousel events.
 */

const EVENT_CATEGORY = 'Product video';
const CONTENT_TYPE = 'product video';

export interface ProductVideoEventArgs {
  contentPosition: number;
  location: MediaLocation;
  playbackId: string;
}

export const trackProductVideoImpression = ({
  contentPosition,
  location,
  playbackId,
  totalOptions,
}: ProductVideoEventArgs & { totalOptions: number }) => {
  deferTracking(() =>
    sendGAEvent({
      event: 'custom_event',
      event_name: 'view_content',
      event_action: 'Impression',
      event_category: EVENT_CATEGORY,
      event_label: CONTENT_TYPE,
      content_type: CONTENT_TYPE,
      item_id: playbackId,
      content_position: String(contentPosition),
      total_options: String(totalOptions),
      location,
    }),
  );
};

interface VideoInteractionArgs extends ProductVideoEventArgs {
  eventAction: string;
  eventDetails: string;
}

const trackProductVideoInteraction = ({
  contentPosition,
  eventAction,
  eventDetails,
  location,
  playbackId,
}: VideoInteractionArgs) => {
  deferTracking(() =>
    sendGAEvent({
      event: 'custom_event',
      event_name: 'content_interaction',
      event_action: eventAction,
      event_category: EVENT_CATEGORY,
      event_label: eventDetails,
      content_type: CONTENT_TYPE,
      event_details: eventDetails,
      item_id: playbackId,
      content_position: String(contentPosition),
      location,
    }),
  );
};

export const trackProductVideoPlay = (args: ProductVideoEventArgs) =>
  trackProductVideoInteraction({ ...args, eventAction: 'Play', eventDetails: 'play video' });

export const trackProductVideoPause = (args: ProductVideoEventArgs) =>
  trackProductVideoInteraction({ ...args, eventAction: 'Pause', eventDetails: 'pause video' });

export const trackProductVideoComplete = (args: ProductVideoEventArgs) =>
  trackProductVideoInteraction({
    ...args,
    eventAction: 'Complete',
    eventDetails: 'complete video',
  });

export const trackProductVideoExpand = (args: ProductVideoEventArgs) =>
  trackProductVideoInteraction({ ...args, eventAction: 'Expand', eventDetails: 'expand video' });
